import { z } from 'zod'

// Grounded against fd-client-api-google-ads source, not the FE migration
// guide (established habit across this epic — the guide covers setup_process/
// integration-assets, not this endpoint at all):
//   app/api/v4/campaigns.py — campaign_serializer, campaign_setup_serializer
// Diffed field-by-field against app/api/v3/campaigns.py's equivalents: every
// top-level Campaign field is identical between v3 and v4 — the only real
// change is latest_campaign_setup (see latestCampaignSetupSchema below).

const mediaItemSchema = z
  .object({
    type: z.string().optional(),
    filename: z.string().optional(),
    url: z.string().optional(),
    youtube_video_id: z.string().nullable().optional(),
    size: z.number().optional(),
    height: z.number().optional(),
    width: z.number().optional(),
    placement_group_id: z.string().nullable().optional(),
  })
  .passthrough()

const integrationDetailSchema = z
  .object({
    id: z.number().optional(),
    partner_id: z.string().optional(),
    platform: z.string().optional(),
    asset_class: z.string().optional(),
    asset_id: z.string().optional(),
    asset_label: z.string().optional(),
  })
  .passthrough()

const creativeSchema = z
  .object({
    // The backend sends explicit `null` for an empty campaign, not omission —
    // every array/string field here needs `.nullable()`, not just `.optional()`.
    destination_url: z.array(z.string()).nullable().optional(),
    headline: z.array(z.string()).nullable().optional(),
    body: z.array(z.string()).nullable().optional(),
    description: z.string().nullable().optional(),
    media: z.array(mediaItemSchema).nullable().optional(),
    placement_media: z.array(mediaItemSchema).nullable().optional(),
    default_locale: z.string().nullable().optional(),
    localizations: z.unknown().optional(),
    integrations: z.array(integrationDetailSchema).nullable().optional(),
  })
  .passthrough()

const suggestedCreativeSchema = z
  .object({
    headline: z.array(z.string()).nullable().optional(),
    body: z.array(z.string()).nullable().optional(),
    media: z
      .array(z.object({ description: z.string().optional() }).passthrough())
      .nullable()
      .optional(),
  })
  .passthrough()

const eventSummarySchema = z
  .object({
    id: z.string().optional(),
    partner_id: z.string().optional(),
    title: z.string().optional(),
    start_date_time: z.string().optional(),
  })
  .passthrough()

// v4 marks this whole field "Obsolete: For backwards compatibility"
// (campaign_serializer's own comment) and both sub-fields are raw
// passthroughs sourced from event_campaign, not the typed v3 model —
// `creative` is the unified (non-platform-native) creative, `targeting` is
// the same platform-nested {fb, google} shape as setup_process.targeting[]
// entries, not v3's flat {targeting_mode, 'postal-codes', ...} model.
// Deliberately left as z.unknown() rather than typed further — no value in
// strongly typing a field already headed for removal, and the real
// replacement (domain/publishedCampaignSetups) already covers the
// non-deprecated equivalent of this data.
const latestCampaignSetupSchema = z
  .object({
    creative: z.unknown().nullable().optional(),
    targeting: z.unknown().nullable().optional(),
  })
  .passthrough()
  .nullable()

export const campaignItemSchema = z
  .object({
    partner_id: z.string(),
    event_title: z.string(),
    eid: z.string(),
    tc: z.string(),
    tc_run_id: z.union([z.string(), z.number()]),
    tc_size: z.number().optional(),
    event_date: z.string().optional(),
    // Nullable columns on `campaign` whose 0.0 default only applies at insert,
    // so a row that never went through scoring carries NULL, not 0.
    buying_power: z.number().nullable().optional(),
    attractiveness: z.number().nullable().optional(),
    potential: z.number().nullable().optional(),
    metric_pred: z.number().nullable().optional(),
    run_time: z.number(),
    credits: z.number().nullable().optional(),
    budget: z.number().nullable().optional(),
    currency: z.string().optional(),
    rank: z.number().nullable().optional(),
    channel: z.string().optional(),
    status: z.string(),
    goal: z.string().nullable().optional(),
    budget_average: z.number().nullable().optional(),
    published: z.boolean().optional(),
    initial_set_up_date: z.string().nullable().optional(),
    fb_adset_end_time: z.string().nullable().optional(),
    manually_created: z.boolean().optional(),
    cr_start_days_upfront: z.number().nullable().optional(),
    cr_end_days_upfront: z.number().nullable().optional(),
    date_last_updated: z.string().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    suggested_creative: suggestedCreativeSchema.nullable().optional(),
    creative: creativeSchema.nullable().optional(),
    event: eventSummarySchema.optional(),
    alias: z.string().optional(),
    latest_campaign_setup: latestCampaignSetupSchema.optional(),
  })
  .passthrough()

// Paginator-driven list response (same convention as every other v4 list
// endpoint) — bare, no {status_code, data} envelope, per 01-01's finding.
export const campaignsResponseSchema = z
  .object({
    total: z.number().optional(),
    items: z.array(campaignItemSchema),
  })
  .passthrough()
